import React, { useEffect, useMemo, useState } from 'react';

export interface AutocompleteOption {
  value: string;
  label: string;
}

export interface AutocompleteDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  allowCustom?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onCommit?: () => void;
  onHoverOption?: (value: string | null) => void;
  disabled?: boolean;
  /** The input's accessible name where no visible label sits beside it. */
  ariaLabel?: string;
}

const AutocompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select or type...',
  allowCustom = true,
  inputRef: inputRefProp,
  onCommit,
  onHoverOption,
  disabled = false,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const localInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? localInputRef;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(opt =>
      opt.value.toLowerCase().includes(lower) ||
      opt.label.toLowerCase().includes(lower)
    );
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        onHoverOption?.(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (val: string) => {
    onChange(val);
    setSearch('');
    setIsOpen(false);
    onHoverOption?.(null);
  };

  // Show the label (not the raw value) when the dropdown is closed
  const displayValue = useMemo(() => {
    if (!value) return '';
    const match = options.find(o => o.value === value);
    return match ? match.label : value;
  }, [value, options]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          value={isOpen ? search : displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => { if (!disabled) setIsOpen(true); }}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
              setSearch('');
              onHoverOption?.(null);
              return;
            }
            if (e.key !== 'Enter' && e.key !== 'Tab') return;
            if (e.key === 'Tab' && e.shiftKey) return;
            e.preventDefault();

            const query = search.trim();
            if (isOpen && query) {
              if (filteredOptions.length) {
                handleSelect(filteredOptions[0].value);
              } else if (allowCustom) {
                handleSelect(query);
              } else {
                setIsOpen(false);
                setSearch('');
              }
            } else {
              setIsOpen(false);
              setSearch('');
            }

            onCommit?.();
          }}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            fontSize: 12,
          }}
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) inputRef.current?.focus();
          }}
          disabled={disabled}
          style={{
            padding: '8px 10px',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            background: 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: 10,
          }}
        >
          ▼
        </button>
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: 200,
          overflowY: 'auto',
          zIndex: 1000,
        }}>
          <div
            onClick={() => handleSelect('')}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 11,
              color: '#9ca3af',
              background: value === '' ? '#f3f4f6' : 'transparent',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            -- None --
          </div>
          {filteredOptions.map((opt, idx) => (
            <div
              key={idx}
              onClick={() => handleSelect(opt.value)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 11,
                background: value === opt.value ? '#dbeafe' : 'transparent',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; onHoverOption?.(opt.value); }}
              onMouseLeave={(e) => { e.currentTarget.style.background = value === opt.value ? '#dbeafe' : 'transparent'; onHoverOption?.(null); }}
            >
              <div style={{ fontWeight: 500 }}>{opt.label}</div>
              {opt.label !== opt.value && (
                <div style={{ fontSize: 10, color: '#6b7280' }}>{opt.value}</div>
              )}
            </div>
          ))}
          {allowCustom && search && !filteredOptions.some(o => o.value === search) && (
            <div
              onClick={() => handleSelect(search)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 11,
                background: '#f0fdf4',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <div style={{ fontWeight: 500, color: '#166534' }}>Use "{search}"</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Custom value</div>
            </div>
          )}
          {!allowCustom && search && filteredOptions.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: '#ef4444' }}>
              No matching option found. Select from the list.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteDropdown;
